import React, { useCallback, useState } from 'react';
import { Alert } from 'react-bootstrap';

import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import Button from '../../../common/components/Button';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { ExamSearch } from '../Exam/ExamSearch';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';

import { AddExamToInvoiceModal } from './AddExamToInvoiceModal';

interface SalesInvoiceExamsProps {
  salesInvoiceId?: string;
  onExamsChanged?: () => void;
  readOnly?: boolean;
}

export const SalesInvoiceExams: React.FC<SalesInvoiceExamsProps> = ({
  salesInvoiceId,
  onExamsChanged,
  readOnly,
}) => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const [showModal, setShowModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcErrors, setCalcErrors] = useState<string[]>([]);
  const [calcSuccess, setCalcSuccess] = useState<string | undefined>();

  const handleCalculatePositions = async () => {
    if (!salesInvoiceId) {
      return;
    }
    setCalcErrors([]);
    setCalcSuccess(undefined);
    setCalcLoading(true);
    try {
      const result = await spd.salesInvoices.calculatePositions(salesInvoiceId);
      if (result.positionsCount === 0) {
        setCalcErrors([
          'Brak danych do wyliczenia pozycji (brak badań lub pozycji cennika)',
        ]);
      } else {
        setCalcSuccess(
          `Wyliczono i zapisano ${result.positionsCount} pozycji faktury`,
        );
        onExamsChanged?.();
      }
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setCalcErrors(errors);
    } finally {
      setCalcLoading(false);
    }
  };

  const handleRemoveExam = useCallback(
    async (examId: string) => {
      if (!salesInvoiceId) {
        return;
      }
      await spd.salesInvoices.removeExam(salesInvoiceId, examId);
      setRefresh(r => !r);
      onExamsChanged?.();
    },
    [salesInvoiceId, spd.salesInvoices, onExamsChanged],
  );

  const getData: GridGetterFunction = useCallback(
    (pageSize, pageNumber, orderBy, orderDirection, filterText) => {
      if (!salesInvoiceId) {
        return Promise.resolve([]);
      }
      return spd.exams.getForSalesInvoiceByPortion(
        salesInvoiceId,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        handleRemoveExam,
      );
    },
    [salesInvoiceId, spd.exams, handleRemoveExam],
  );

  const getCount: GridCountFunction = useCallback(
    (filterText?: string) => {
      if (!salesInvoiceId) {
        return Promise.resolve(0);
      }
      return spd.exams.getForSalesInvoiceCount(filterText, salesInvoiceId);
    },
    [salesInvoiceId, spd.exams],
  );

  const handleRowClick = (id: string) => {
    void nav.examTests(id);
  };

  const handleConfirm = async (examIds: string[]) => {
    if (!salesInvoiceId) {
      return;
    }
    await spd.salesInvoices.addExams(salesInvoiceId, examIds);
    setShowModal(false);
    setRefresh(r => !r);
    onExamsChanged?.();
  };

  return (
    <>
      <div className="d-flex gap-2 mb-2">
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!salesInvoiceId}
          onClick={() => setShowModal(true)}
        >
          Dodaj badanie
        </Button>
        {!readOnly && (
          <ConfirmationButton
            variant="outline-secondary"
            disabled={!salesInvoiceId}
            isLoading={calcLoading}
            onOK={() => void handleCalculatePositions()}
            confirmation="Wyliczenie pozycji zastąpi wszystkie istniejące pozycje faktury. Czy kontynuować?"
          >
            Wylicz pozycje
          </ConfirmationButton>
        )}
      </div>
      {calcSuccess && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setCalcSuccess(undefined)}
          className="mb-2"
        >
          {calcSuccess}
        </Alert>
      )}
      <ValidationAlert
        show={calcErrors.length > 0}
        errors={calcErrors}
        onHide={() => setCalcErrors([])}
      />
      <ExamSearch
        getData={getData}
        getCount={getCount}
        onRowClick={handleRowClick}
        withFilterInColumns={false}
        withoutButtons={true}
        refresh={refresh}
        onRemove={examId => void handleRemoveExam(examId)}
      />
      <AddExamToInvoiceModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={examIds => void handleConfirm(examIds)}
      />
    </>
  );
};
